/** Regale (Screen laden/Regale): Liste mit Filtern, Anlegen/Bearbeiten. QR-Schilder, Leistung und Warteliste sind V2. */
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select } from "../ui/Layout";
import { Textarea } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { useRegalAendern, useRegalAnlegen, useRegale, useRegalklassen, type RegalEingabe, type RegalZeile } from "./regale-api";

export const BELEGUNG_TEXT: Record<RegalZeile["belegung"], { badge: "paid" | "wait" | "attention" | "neutral" | "shelf"; text: string }> = {
  frei: { badge: "shelf", text: "Frei" },
  belegt: { badge: "paid", text: "Belegt" },
  bald_frei: { badge: "attention", text: "Bald frei" },
  reserviert: { badge: "wait", text: "Reserviert" },
  inaktiv: { badge: "neutral", text: "Nicht aktiv" },
};

export function klassenPreisText(k: { preisWocheCent: number | null; preisMonatCent: number | null } | null | undefined): string {
  if (!k) return "–";
  const teile = [k.preisMonatCent !== null ? `${formatEuro(k.preisMonatCent)} / Monat` : null, k.preisWocheCent !== null ? `${formatEuro(k.preisWocheCent)} / Woche` : null].filter(Boolean);
  return teile.join(" · ") || "–";
}

export function Regale() {
  const regale = useRegale();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"alle" | "belegt" | "frei" | "bald_frei" | "reserviert">("alle");
  const [lage, setLage] = useState("");
  const [suche, setSuche] = useState("");
  const [dialog, setDialog] = useState<null | { regal?: RegalZeile }>(null);

  const liste = regale.data?.regale ?? [];
  const lagen = useMemo(() => [...new Set(liste.map((r) => r.lage).filter(Boolean))].sort(), [liste]);
  const gefiltert = liste.filter((r) => {
    if (filter !== "alle" && r.belegung !== filter) return false;
    if (lage && r.lage !== lage) return false;
    if (suche) {
      const q = suche.toLowerCase();
      if (!r.nummer.includes(q) && !(r.aktuell?.mieterName.toLowerCase().includes(q) ?? false) && !(r.naechste?.mieterName.toLowerCase().includes(q) ?? false)) return false;
    }
    return true;
  });
  const zaehler = (b: RegalZeile["belegung"]) => liste.filter((r) => r.belegung === b).length;
  const aktiv = liste.filter((r) => r.aktiv).length;
  const belegt = zaehler("belegt") + zaehler("bald_frei");

  return (
    <>
      <PageHeader
        kicker="Regale"
        title="Regale"
        sub={aktiv ? `${aktiv} Regale · ${belegt} belegt · Auslastung ${Math.round((belegt / aktiv) * 100)} %` : "Noch keine Regale"}
        actions={
          <>
            <Link to="/laden/regale/plan" className="rg-btn rg-btn--secondary">Ladenplan</Link>
            <Link to="/laden/belegung" className="rg-btn rg-btn--secondary">Belegung</Link>
            <a href="/api/laden/regal-schilder.pdf?format=a6" target="_blank" rel="noreferrer" className="rg-btn rg-btn--secondary">Alle Schilder (PDF)</a>
            <Button variant="primary" icon="plus" onClick={() => setDialog({})}>Regal anlegen</Button>
          </>
        }
      />
      <div className="sc-toolbar" style={{ marginBottom: "var(--space-4)" }}>
        {(
          [
            ["alle", `Alle ${liste.length}`],
            ["belegt", `Belegt ${zaehler("belegt")}`],
            ["frei", `Frei ${zaehler("frei")}`],
            ["bald_frei", `Bald frei ${zaehler("bald_frei")}`],
            ["reserviert", `Reserviert ${zaehler("reserviert")}`],
          ] as [typeof filter, string][]
        ).map(([k, t]) => (
          <button key={k} type="button" className="sc-pill" aria-pressed={filter === k} onClick={() => setFilter(k)}>{t}</button>
        ))}
        <span className="sc-grow" />
        {lagen.length ? (
          <Select label="Lage" value={lage} onChange={(e) => setLage(e.target.value)}>
            <option value="">Alle Lagen</option>
            {lagen.map((l) => <option key={l} value={l}>{l}</option>)}
          </Select>
        ) : null}
        <TextField label="Suche" icon="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Nr. oder Mieter" />
      </div>
      <Seitenzustand query={regale} istLeer={(d) => d.regale.length === 0} leer={<EmptyState illustration="empty-shelf" title="Noch keine Regale" action={<><Button variant="primary" icon="plus" onClick={() => setDialog({})}>Regal anlegen</Button><Link to="/laden/einrichtung" className="rg-btn rg-btn--secondary">Viele auf einmal (Einrichtung)</Link></>}>Regalnummern sind dreistellig und stehen im Artikelcode (1234-055-025).</EmptyState>}>
        {() => (
          <Card flush>
            <div className="app-tabellen-scroll">
              <table className="rg-table">
                <thead>
                  <tr><th>Nr.</th><th>Größe</th><th>Lage</th><th>Klasse</th><th>Kategorie</th><th>Status</th><th>Mieter</th><th>Bis</th><th>Miete</th><th /></tr>
                </thead>
                <tbody>
                  {gefiltert.length === 0 ? (
                    <tr><td colSpan={10} className="rg-table__empty">Kein Regal passt zu den Filtern.</td></tr>
                  ) : (
                    gefiltert.map((r) => {
                      const b = r.aktuell ?? r.naechste;
                      const st = BELEGUNG_TEXT[r.belegung];
                      return (
                        <tr key={r.id} className="rg-row--click" onClick={() => navigate(`/laden/regale/${r.id}`)}>
                          <td className="sc-num sc-strong">{r.nummer}</td>
                          <td>{r.groesse || r.klasse?.groesseText || "–"}</td>
                          <td>{r.lage || "–"}</td>
                          <td>{r.klasse?.name ?? <span className="sc-muted">keine</span>}</td>
                          <td>{r.kategorie || "–"}</td>
                          <td><StatusBadge status={st.badge}>{st.text}{!r.aktuell && r.naechste ? ` ab ${formatTag(r.naechste.start)}` : ""}</StatusBadge></td>
                          <td>{b ? b.mieterName : "–"}</td>
                          <td>{r.aktuell ? (r.aktuell.ende ? formatTag(r.aktuell.ende) : "Abo") : "–"}</td>
                          <td className="sc-num">{klassenPreisText(r.klasse)}</td>
                          <td><Link to={`/laden/regale/${r.id}`} className="rg-btn rg-btn--quiet" onClick={(e) => e.stopPropagation()}>Öffnen</Link></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Seitenzustand>
      {dialog ? <RegalFormular regal={dialog.regal} onClose={() => setDialog(null)} /> : null}
    </>
  );
}

/** Dialog Regal anlegen/bearbeiten (auch vom Regal-Detail genutzt). */
export function RegalFormular({ regal, onClose }: { regal?: Pick<RegalZeile, "id" | "nummer" | "regalklasseId" | "groesse" | "lage" | "kategorie" | "notiz" | "aktiv">; onClose: () => void }) {
  const klassen = useRegalklassen();
  const anlegen = useRegalAnlegen();
  const aendern = useRegalAendern();
  const [form, setForm] = useState<RegalEingabe>({ nummer: regal?.nummer ?? "", regalklasseId: regal?.regalklasseId ?? null, groesse: regal?.groesse ?? "", lage: regal?.lage ?? "", kategorie: regal?.kategorie ?? "", notiz: regal?.notiz ?? "", aktiv: regal?.aktiv ?? true });
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!/^\d{3}$/.test(form.nummer)) return setFehler("Regalnummer ist dreistellig, z. B. 055.");
    try {
      if (regal) await aendern.mutateAsync({ id: regal.id, ...form });
      else await anlegen.mutateAsync(form);
      onClose();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Speichern hat nicht geklappt.");
    }
  }

  return (
    <Dialog open title={regal ? `Regal ${regal.nummer} bearbeiten` : "Regal anlegen"} onClose={onClose} actions={<><Button variant="quiet" onClick={onClose}>Abbrechen</Button><Button variant="primary" type="submit" form="regal-form" loading={anlegen.isPending || aendern.isPending}>Speichern</Button></>}>
      <form id="regal-form" className="app-form" onSubmit={absenden} noValidate>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <FormGrid>
          <TextField label="Regalnummer" value={form.nummer} onChange={(e) => setForm({ ...form, nummer: e.target.value })} inputMode="numeric" placeholder="055" hint="Dreistellig, steht auf den Etiketten" autoFocus={!regal} />
          <Select label="Regalklasse" value={form.regalklasseId ?? ""} onChange={(e) => setForm({ ...form, regalklasseId: e.target.value || null })} hint="Bestimmt den Mietpreis">
            <option value="">Keine Klasse</option>
            {(klassen.data?.klassen ?? []).filter((k) => k.aktiv || k.id === form.regalklasseId).map((k) => <option key={k.id} value={k.id}>{k.name} · {klassenPreisText(k)}</option>)}
          </Select>
          <TextField label="Größe" value={form.groesse} onChange={(e) => setForm({ ...form, groesse: e.target.value })} placeholder="z. B. 80 × 180 cm, 4 Böden" />
          <TextField label="Lage" value={form.lage} onChange={(e) => setForm({ ...form, lage: e.target.value })} placeholder="z. B. Wand links, Kassennähe" />
          <TextField label="Kategorie" value={form.kategorie} onChange={(e) => setForm({ ...form, kategorie: e.target.value })} placeholder="z. B. Deko, Kleidung" />
        </FormGrid>
        <Textarea label="Notiz (intern)" value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} rows={2} />
        {regal ? <label className="sc-check"><input type="checkbox" checked={form.aktiv} onChange={(e) => setForm({ ...form, aktiv: e.target.checked })} /> Regal ist aktiv (buchbar)</label> : null}
      </form>
    </Dialog>
  );
}
